import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, ShieldAlert } from "lucide-react";
import { clinics } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOPICS = [
  { value: "bokning", label: "Bokning eller ombokning" },
  { value: "behandling", label: "Allmän fråga om behandling" },
  { value: "praktiskt", label: "Praktisk fråga (hitta hit, betalning m.m.)" },
  { value: "ovrigt", label: "Övrigt" },
];
const EMPTY = { name: "", phone: "", email: "", clinic: "", topic: "", message: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s-]{5,19}$/;

const validate = (v) => {
  const e = {};
  if (v.name.trim().length < 2) e.name = "Skriv ditt namn.";
  if (!v.phone.trim() && !v.email.trim()) e.contact = "Ange telefon eller e-post så att vi kan nå dig.";
  if (v.phone.trim() && !PHONE_RE.test(v.phone.trim())) e.phone = "Kontrollera telefonnumret.";
  if (v.email.trim() && !EMAIL_RE.test(v.email.trim())) e.email = "Kontrollera e-postadressen.";
  if (!v.clinic) e.clinic = "Välj mottagning.";
  if (!v.topic) e.topic = "Välj ärende.";
  if (v.message.length > 500) e.message = "Max 500 tecken.";
  return e;
};

const Field = ({ id, label, error, hint, children }) => (
  <div>
    <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-ink">{label}</label>
    {children}
    {hint && !error && <p className="mt-1.5 text-xs text-muted">{hint}</p>}
    {error && <p id={`${id}-error`} className="mt-1.5 text-sm text-[#9A3B2E]" data-testid={`contact-${id}-error`}>{error}</p>}
  </div>
);

export default function ContactForm() {
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const set = (k) => (ev) => setV({ ...v, [k]: ev.target.value });
  const aria = (k) => ({ "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `${k}-error` : undefined });

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate(v);
    setErrors(e);
    if (Object.keys(e).length) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, {
        name: v.name.trim(), phone: v.phone.trim() || null, email: v.email.trim() || null,
        clinic: v.clinic, topic: v.topic, message: v.message.trim() || null,
      });
      setStatus("success");
      setV(EMPTY);
    } catch {
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div role="status" className="rounded-[28px] bg-white p-8 ring-1 ring-line" data-testid="contact-success">
        <CheckCircle2 className="h-10 w-10 text-pine" aria-hidden="true" />
        <h3 className="mt-4 font-serif text-3xl text-ink">Tack, vi har tagit emot ditt meddelande</h3>
        <p className="mt-2 text-muted">Vi kontaktar dig via telefon eller e-post. Vill du boka direkt kan du göra det via BokaDirekt.</p>
        <button type="button" onClick={() => setStatus("idle")} className="mt-6 text-sm font-semibold text-pine underline underline-offset-4" data-testid="contact-send-another">Skicka ett nytt meddelande</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="grid gap-5 rounded-[28px] bg-white p-6 ring-1 ring-line sm:p-8" data-testid="contact-form">
      <Field id="name" label="Namn" error={errors.name}>
        <input id="name" autoComplete="name" value={v.name} onChange={set("name")} {...aria("name")} className="np-input" data-testid="contact-name-input" />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="phone" label="Telefon" error={errors.phone}>
          <input id="phone" type="tel" autoComplete="tel" value={v.phone} onChange={set("phone")} {...aria("phone")} className="np-input" data-testid="contact-phone-input" />
        </Field>
        <Field id="email" label="E-post" error={errors.email}>
          <input id="email" type="email" autoComplete="email" value={v.email} onChange={set("email")} {...aria("email")} className="np-input" data-testid="contact-email-input" />
        </Field>
      </div>
      <p className={`-mt-2 text-xs ${errors.contact ? "text-sm text-[#9A3B2E]" : "text-muted"}`} role={errors.contact ? "alert" : undefined} data-testid="contact-contact-hint">
        {errors.contact || "Fyll i minst ett av fälten telefon eller e-post."}
      </p>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="clinic" label="Mottagning" error={errors.clinic}>
          <select id="clinic" value={v.clinic} onChange={set("clinic")} {...aria("clinic")} className="np-input" data-testid="contact-clinic-select">
            <option value="">Välj…</option>
            {clinics.map((c) => <option key={c.id} value={c.id}>{c.city}</option>)}
            <option value="vet-ej">Vet inte än</option>
          </select>
        </Field>
        <Field id="topic" label="Ärende" error={errors.topic}>
          <select id="topic" value={v.topic} onChange={set("topic")} {...aria("topic")} className="np-input" data-testid="contact-topic-select">
            <option value="">Välj…</option>
            {TOPICS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
      </div>
      <Field id="message" label="Kort meddelande (valfritt)" error={errors.message}>
        <textarea id="message" rows={3} maxLength={500} value={v.message} onChange={set("message")} {...aria("message")} className="np-input min-h-[96px] py-3" data-testid="contact-message-input" />
      </Field>
      <p className="-mt-2 flex gap-2 text-xs leading-relaxed text-muted" data-testid="contact-privacy-note">
        <ShieldAlert className="h-4 w-4 shrink-0 text-pine" aria-hidden="true" />
        Skriv inte personnummer, sjukdomshistorik eller annan känslig medicinsk information här. Det går vi igenom vid besöket.
      </p>
      {status === "error" && (
        <p role="alert" className="rounded-xl bg-[#F6E3DE] p-3 text-sm text-[#7A2E22]" data-testid="contact-error">
          Något gick fel och meddelandet skickades inte. Försök igen eller ring oss.
        </p>
      )}
      <button type="submit" disabled={status === "sending"} className="np-btn inline-flex h-12 items-center justify-center gap-2 rounded-full bg-forest px-8 font-semibold text-cream hover:bg-pine disabled:opacity-70 sm:justify-self-start" data-testid="contact-submit-button">
        {status === "sending" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        Skicka meddelande
      </button>
    </form>
  );
}
